import { Component, EventEmitter, Input, Output, signal, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CreateDetectionSequencePatternRequest, DetectionPointDto, SequenceAction, PointDirection, PlatePosition, ParkingZoneDto } from '../../../../../api/gen';
import { ParkingZoneService } from '../../../../../api/gen/api/parkingZone.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-create-pattern-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatProgressBarModule,
        MatCheckboxModule
    ],
    templateUrl: './create-pattern-form.component.html',
    styleUrls: ['./create-pattern-form.component.scss']
})
export class CreatePatternFormComponent implements OnChanges {
    @Input() isProcessing = false;
    @Input() detectionPoints: DetectionPointDto[] = [];
    @Input() parkingSpaceId: string | null = null;
    @Output() createPattern = new EventEmitter<CreateDetectionSequencePatternRequest>();
    @Output() cancel = new EventEmitter<void>();

    patternForm!: FormGroup;
    sequenceActions = Object.values(SequenceAction);
    pointDirections = Object.values(PointDirection);
    platePositions = Object.values(PlatePosition);
    availableZones = signal<ParkingZoneDto[]>([]);
    isLoadingZones = signal(false);

    constructor(private fb: FormBuilder, private parkingZoneService: ParkingZoneService) {
        this.initForm();
        this.loadAvailableZones();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['parkingSpaceId']) {
            this.loadAvailableZones();
        }
    }

    private initForm(): void {
        this.patternForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(3)]],
            description: [''],
            targetZoneId: [''], // empty string means "outside parking space"
            sourceZoneId: [''], // empty string means "outside parking space" 
            action: [SequenceAction.LogOnly, Validators.required],
            maxSequenceDurationSeconds: [300, [Validators.min(1)]],
            priority: [1, [Validators.min(0)]],
            isActive: [true],
            steps: this.fb.array([])
        });
        this.addStep();
    }

    private async loadAvailableZones(): Promise<void> {
        if (!this.parkingSpaceId) {
            this.availableZones.set([]);
            this.isLoadingZones.set(false);
            return;
        }
        this.isLoadingZones.set(true);
        try {
            const zonesResult = await firstValueFrom(
                this.parkingZoneService.parkingZoneFilterPost()
            );
            const allZones = (zonesResult as any)?.items || [];
            const filteredZones = allZones.filter((zone: ParkingZoneDto) => zone.parkingSpaceId === this.parkingSpaceId);
            this.availableZones.set(filteredZones);
        } catch (error) {
            console.warn('Could not load parking zones:', error);
        } finally {
            this.isLoadingZones.set(false);
        }
    }

    get steps(): FormArray {
        return this.patternForm.get('steps') as FormArray;
    }

    createStep(): FormGroup {
        return this.fb.group({
            detectionPointId: ['', Validators.required],
            stepOrder: [this.steps.length + 1, [Validators.required, Validators.min(1)]],
            expectedDirection: [PointDirection.Approach, Validators.required],
            platePosition: [PlatePosition.Front, Validators.required],
            isRequired: [true]
        });
    } addStep(): void {
        this.steps.push(this.createStep());
    }

    removeStep(index: number): void {
        this.steps.removeAt(index);
        // Update stepOrder of subsequent steps
        for (let i = index; i < this.steps.length; i++) {
            this.steps.at(i).get('stepOrder')?.setValue(i + 1);
        }
    }

    onSubmit(): void {
        if (this.patternForm.valid) {
            const formValue = this.patternForm.getRawValue();

            // Debug logging to trace the zone id issue
            console.log('Raw form values:', {
                targetZoneId: formValue.targetZoneId,
                sourceZoneId: formValue.sourceZoneId,
                targetZoneType: typeof formValue.targetZoneId,
                sourceZoneType: typeof formValue.sourceZoneId
            });

            // Handle zone IDs properly - ensure empty strings become null
            const targetZoneId = formValue.targetZoneId === '' ? null : formValue.targetZoneId;
            const sourceZoneId = formValue.sourceZoneId === '' ? null : formValue.sourceZoneId;

            console.log('Processed zone IDs:', { targetZoneId, sourceZoneId });

            const request: CreateDetectionSequencePatternRequest = {
                name: formValue.name,
                description: formValue.description,
                targetZoneId: targetZoneId,
                sourceZoneId: sourceZoneId,
                parkingSpaceId: this.parkingSpaceId,
                action: formValue.action,
                maxSequenceDurationSeconds: formValue.maxSequenceDurationSeconds,
                priority: formValue.priority,
                isActive: formValue.isActive,
                steps: formValue.steps.map((step: any) => ({
                    detectionPointId: step.detectionPointId,
                    stepOrder: step.stepOrder,
                    expectedDirection: step.expectedDirection,
                    platePosition: step.platePosition,
                    isRequired: step.isRequired
                }))
            };
            console.log('Submitting pattern creation request:', request);
            this.createPattern.emit(request);
        }
    }

    onCancel(): void {
        this.patternForm.reset({
            targetZoneId: '',
            sourceZoneId: '',
            action: SequenceAction.LogOnly,
            maxSequenceDurationSeconds: 300,
            priority: 1,
            isActive: true
        });
        this.steps.clear();
        this.addStep();
        this.cancel.emit();
    }
}
